import { useEffect, useState } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { Helmet } from "react-helmet-async"
import { ArrowLeft, Calendar, Clock, ChevronRight, Mail, Phone, Globe, MapPin, BookOpen, Linkedin } from "lucide-react"
import { loadPublicAuthor, clearAuthor } from "../store/slices/publicSlice"
import { fetchPublicPosts } from "../api/publicApi"
import { getCategoryLabel } from "../utils/categoryLabels"

function estimateReadTime(html) {
    const text = html?.replace(/<[^>]+>/g, '') || ''
    return `${Math.max(1, Math.ceil(text.split(/\s+/).filter(Boolean).length / 200))} min`
}

export default function AuthorProfile() {
    const { id } = useParams()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { author, loading } = useSelector((s) => s.public)
    const [posts, setPosts] = useState([])
    const [postsLoading, setPostsLoading] = useState(true)

    useEffect(() => {
        dispatch(loadPublicAuthor(id))
        fetchPublicPosts({ author: id, limit: 50 }).then((res) => {
            setPosts(res.posts || [])
            setPostsLoading(false)
        }).catch(() => setPostsLoading(false))
        return () => { dispatch(clearAuthor()) }
    }, [dispatch, id])

    if (loading && !author) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-pulse space-y-6 max-w-4xl w-full px-6">
                    <div className="flex items-center gap-6"><div className="w-24 h-24 bg-secondary rounded-full" /><div className="space-y-2 flex-1"><div className="h-6 bg-secondary rounded w-1/3" /><div className="h-4 bg-secondary rounded w-1/2" /></div></div>
                    <div className="grid md:grid-cols-3 gap-6">{[1,2,3].map(i => <div key={i} className="h-64 bg-secondary rounded-xl" />)}</div>
                </div>
            </div>
        )
    }

    if (!author) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-foreground mb-4">Auteur non trouvé</h1>
                    <button onClick={() => navigate(-1)} className="px-6 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 transition-all">Retour</button>
                </div>
            </div>
        )
    }

    const roleLabels = { professor: 'Professeur', student: 'Étudiant', 'space-student': 'Espace Étudiant', admin: 'Administrateur', moderator: 'Modérateur' }

    return (
        <div className="min-h-screen bg-background">
            <Helmet>
                <title>{author.fullname} - Université de Ngozi</title>
                <meta name="description" content={author.biography || `Profil de ${author.fullname}`} />
            </Helmet>

            {/* Hero */}
            <section className="relative py-16 bg-gradient-to-br from-primary/5 via-background to-accent/5 overflow-hidden">
                <div className="absolute top-10 right-10 w-32 h-32 bg-primary/5 rounded-full blur-3xl" />
                <div className="max-w-5xl mx-auto px-4 sm:px-6 relative">
                    <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8">
                        <ArrowLeft className="w-4 h-4" /> Retour
                    </button>

                    <div className="flex flex-col md:flex-row items-start gap-8">
                        {author.profilePicture ? (
                            <img src={author.profilePicture} alt={author.fullname} className="w-28 h-28 rounded-2xl object-cover border-2 border-border shadow-lg" />
                        ) : (
                            <div className="w-28 h-28 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white text-4xl font-bold shadow-lg">
                                {author.fullname.charAt(0)}
                            </div>
                        )}

                        <div className="flex-1">
                            <h1 className="text-3xl font-bold text-foreground mb-2">{author.fullname}</h1>
                            <div className="flex flex-wrap items-center gap-3 mb-4">
                                <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-semibold">
                                    {roleLabels[author.role] || author.role}
                                </span>
                                {author.department && (
                                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                        <MapPin className="w-3 h-3" /> {author.department}
                                    </span>
                                )}
                                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <BookOpen className="w-3 h-3" /> {author.postCount || 0} articles publiés
                                </span>
                            </div>

                            {author.biography && (
                                <p className="text-muted-foreground leading-relaxed mb-4 max-w-2xl">{author.biography}</p>
                            )}

                            <div className="flex flex-wrap gap-3">
                                {author.email && (
                                    <a href={`mailto:${author.email}`} className="inline-flex items-center gap-2 px-3 py-1.5 bg-card border border-border rounded-lg text-xs font-medium text-muted-foreground hover:text-primary hover:border-primary/30 transition-all">
                                        <Mail className="w-3.5 h-3.5" /> Email
                                    </a>
                                )}
                                {author.linkedin && (
                                    <a href={author.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-1.5 bg-card border border-border rounded-lg text-xs font-medium text-muted-foreground hover:text-blue-700 hover:border-blue-200 transition-all">
                                        <Linkedin className="w-3.5 h-3.5" /> LinkedIn
                                    </a>
                                )}
                                {author.website && (
                                    <a href={author.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-1.5 bg-card border border-border rounded-lg text-xs font-medium text-muted-foreground hover:text-primary hover:border-primary/30 transition-all">
                                        <Globe className="w-3.5 h-3.5" /> Site web
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Articles */}
            <section className="py-12">
                <div className="max-w-5xl mx-auto px-4 sm:px-6">
                    <h2 className="text-2xl font-bold text-foreground mb-8">
                        Articles de {author.fullname}
                    </h2>

                    {postsLoading ? (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[1,2,3].map(i => <div key={i} className="h-64 bg-secondary rounded-xl animate-pulse" />)}
                        </div>
                    ) : posts.length === 0 ? (
                        <div className="text-center py-16 bg-card rounded-xl border border-border">
                            <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <p className="text-muted-foreground">Aucun article publié pour le moment.</p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {posts.map((p) => {
                                const img = p.headlineImage?.url || p.headlineImage || ''
                                return (
                                    <article key={p._id} onClick={() => navigate(`/article/${p._id}`)} className="group cursor-pointer bg-card rounded-xl border border-border hover:shadow-lg transition-all overflow-hidden">
                                        {img && (
                                            <div className="aspect-video relative bg-gradient-to-br from-primary/10 to-accent/10 overflow-hidden">
                                                <img src={img} alt={p.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                            </div>
                                        )}
                                        <div className="p-5">
                                            <span className="inline-block px-2 py-0.5 bg-primary/10 text-primary rounded text-xs font-semibold mb-2">
                                                {getCategoryLabel(p.category)}
                                            </span>
                                            <h3 className="text-base font-bold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">{p.title}</h3>
                                            <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{p.excerpt}</p>
                                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                                                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(p.publishedAt || p.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
                                                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {estimateReadTime(p.body)}</span>
                                            </div>
                                        </div>
                                    </article>
                                )
                            })}
                        </div>
                    )}
                </div>
            </section>
        </div>
    )
}
